import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Autocomplete,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  useTheme,
  alpha,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  AddCircleOutline as CreateIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import wineNameService, {
  ProducerOption,
  RegionOption,
  GrapeOption,
  WineName,
  WineColor,
  WineType,
  WineSubType,
  VintageConfig,
} from '../../../services/wineNameService';

// ── filter factories ──────────────────────────────────────────────────────────
const filterProducers = createFilterOptions<ProducerOption>();
const filterRegions = createFilterOptions<RegionOption>();
const filterGrapes = createFilterOptions<GrapeOption>();

// ── display label maps ────────────────────────────────────────────────────────
const colorLabels: Record<WineColor, string> = {
  RED: 'Red', WHITE: 'White', ROSE: 'Rosé', ORANGE: 'Orange',
  SPARKLING: 'Sparkling', DESSERT: 'Dessert', FORTIFIED: 'Fortified',
};

const typeLabels: Record<WineType, string> = {
  WINE: 'Wine', SPIRITS: 'Spirits', BEER: 'Beer', OTHER: 'Other',
};

const subTypeLabels: Record<WineSubType, string> = {
  STILL: 'Still', SPARKLING: 'Sparkling', FORTIFIED: 'Fortified',
  DESSERT: 'Dessert', SWEET: 'Sweet', NATURAL: 'Natural',
};

// ── helpers ───────────────────────────────────────────────────────────────────
function buildDisplayNamePreview(
  producer: ProducerOption | null,
  region: RegionOption | null,
  grapes: GrapeOption[],
  specialName: string
): string {
  const parts: string[] = [];
  if (producer) parts.push(producer.fullName || producer.name);
  grapes.forEach((g) => parts.push(g.primaryName));
  if (region) parts.push(region.name);
  if (specialName.trim()) parts.push(specialName.trim());

  const seen = new Set<string>();
  return parts
    .filter((p) => {
      const key = p.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .join(' ');
}

// ── component ─────────────────────────────────────────────────────────────────
const WineNameForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditMode = Boolean(id);
  const theme = useTheme();

  // ── identity / naming fields ───────────────────────────────────────────────
  const [producer, setProducer] = useState<ProducerOption | null>(null);
  const [region, setRegion] = useState<RegionOption | null>(null);
  const [labelGrapes, setLabelGrapes] = useState<GrapeOption[]>([]);
  const [specialName, setSpecialName] = useState('');

  // ── classification fields ──────────────────────────────────────────────────
  const [color, setColor] = useState<WineColor | ''>('');
  const [wineType, setWineType] = useState<WineType>('WINE');
  const [subType, setSubType] = useState<WineSubType | ''>('');
  const [designation, setDesignation] = useState('');
  const [classification, setClassification] = useState('');

  // ── vintage fields ─────────────────────────────────────────────────────────
  const [vintageConfig, setVintageConfig] = useState<VintageConfig | ''>('');
  const [firstVintage, setFirstVintage] = useState('');
  const [finalVintage, setFinalVintage] = useState('');

  // ── other ──────────────────────────────────────────────────────────────────
  const [lwinId, setLwinId] = useState('');
  const [active, setActive] = useState(true);

  // ── autocomplete option lists ──────────────────────────────────────────────
  const [producerOptions, setProducerOptions] = useState<ProducerOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<RegionOption[]>([]);
  const [grapeOptions, setGrapeOptions] = useState<GrapeOption[]>([]);

  // ── loading states ─────────────────────────────────────────────────────────
  const [loadingPage, setLoadingPage] = useState(false);
  const [loadingProducers, setLoadingProducers] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingGrapes, setLoadingGrapes] = useState(false);
  const [creatingOnFly, setCreatingOnFly] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── UX states ──────────────────────────────────────────────────────────────
  const [errors, setErrors] = useState<{ producer?: string; region?: string; color?: string }>({});
  const [duplicate, setDuplicate] = useState<WineName | null>(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // ── load existing record in edit mode ──────────────────────────────────────
  useEffect(() => {
    if (!isEditMode || !id) return;
    setLoadingPage(true);
    wineNameService
      .getById(id)
      .then((wn) => {
        const p = wn.producer;
        setProducer({ _id: p._id, name: p.name, fullName: p.fullName, needsReview: p.needsReview });
        if (wn.region) {
          const r = wn.region;
          setRegion({ _id: r._id, name: r.name, hierarchyNames: r.hierarchyNames, needsReview: r.needsReview });
        }
        if (wn.labelGrapes) {
          setLabelGrapes(
            wn.labelGrapes.map((g) => ({
              _id: g._id,
              primaryName: g.primaryName,
              color: g.color,
              needsReview: g.needsReview,
            }))
          );
        }
        setSpecialName(wn.specialName ?? '');
        setColor(wn.color ?? '');
        setWineType(wn.type ?? 'WINE');
        setSubType(wn.subType ?? '');
        setDesignation(wn.designation ?? '');
        setClassification(wn.classification ?? '');
        setVintageConfig(wn.vintageConfig ?? '');
        setFirstVintage(wn.firstVintage != null ? String(wn.firstVintage) : '');
        setFinalVintage(wn.finalVintage != null ? String(wn.finalVintage) : '');
        setLwinId(wn.lwinId ?? '');
        setActive(wn.active);
      })
      .catch((err) => console.error('Failed to load wine name:', err))
      .finally(() => setLoadingPage(false));
  }, [isEditMode, id]);

  // ── debounced search callbacks ─────────────────────────────────────────────
  const searchProducers = useMemo(
    () =>
      debounce(async (q: string) => {
        setLoadingProducers(true);
        try {
          setProducerOptions(await wineNameService.searchProducers(q));
        } finally {
          setLoadingProducers(false);
        }
      }, 300),
    []
  );

  const searchRegions = useMemo(
    () =>
      debounce(async (q: string) => {
        setLoadingRegions(true);
        try {
          setRegionOptions(await wineNameService.searchRegions(q));
        } finally {
          setLoadingRegions(false);
        }
      }, 300),
    []
  );

  const searchGrapes = useMemo(
    () =>
      debounce(async (q: string) => {
        setLoadingGrapes(true);
        try {
          setGrapeOptions(await wineNameService.searchGrapes(q));
        } finally {
          setLoadingGrapes(false);
        }
      }, 300),
    []
  );

  // ── duplicate check ────────────────────────────────────────────────────────
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!producer) { setDuplicate(null); return; }
    if (dupCheckTimer.current) clearTimeout(dupCheckTimer.current);
    dupCheckTimer.current = setTimeout(async () => {
      setCheckingDuplicate(true);
      try {
        const result = await wineNameService.checkDuplicate({
          producerId: producer._id,
          regionId: region?._id,
          specialName: specialName.trim() || undefined,
          labelGrapeIds: labelGrapes.map((g) => g._id),
        });
        if (result.isDuplicate && result.existing && result.existing._id !== id) {
          setDuplicate(result.existing);
        } else {
          setDuplicate(null);
        }
      } catch { /* ignore */ } finally {
        setCheckingDuplicate(false);
      }
    }, 600);
  }, [producer, region, specialName, labelGrapes, id]);

  // ── create-on-the-fly handlers ─────────────────────────────────────────────
  const handleProducerChange = useCallback(
    async (_: any, newValue: ProducerOption | null) => {
      if (!newValue) { setProducer(null); return; }
      if (newValue.__isNew__) {
        setCreatingOnFly(true);
        try {
          const created = await wineNameService.createProducerOnFly(newValue.inputValue!);
          setProducer(created);
          setProducerOptions((prev) => [created, ...prev]);
        } catch { alert('Failed to create producer. Please try again.'); }
        finally { setCreatingOnFly(false); }
      } else {
        setProducer(newValue);
      }
      if (errors.producer) setErrors((prev) => ({ ...prev, producer: undefined }));
    },
    [errors.producer]
  );

  const handleRegionChange = useCallback(async (_: any, newValue: RegionOption | null) => {
    if (!newValue) { setRegion(null); return; }
    if (newValue.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await wineNameService.createRegionOnFly(newValue.inputValue!);
        setRegion(created);
        setRegionOptions((prev) => [created, ...prev]);
      } catch { alert('Failed to create region. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setRegion(newValue);
    }
    if (errors.region) setErrors((prev) => ({ ...prev, region: undefined }));
  }, [errors.region]);

  const handleGrapesChange = useCallback(async (_: any, newValues: GrapeOption[]) => {
    if (newValues.length > 3) return;
    const lastItem = newValues[newValues.length - 1];
    if (lastItem?.__isNew__) {
      setCreatingOnFly(true);
      try {
        const created = await wineNameService.createGrapeOnFly(lastItem.inputValue!);
        setLabelGrapes([...newValues.slice(0, -1), created]);
        setGrapeOptions((prev) => [created, ...prev]);
      } catch { alert('Failed to create grape. Please try again.'); }
      finally { setCreatingOnFly(false); }
    } else {
      setLabelGrapes(newValues);
    }
  }, []);

  // ── validation & submit ────────────────────────────────────────────────────
  const validate = (): boolean => {
    const newErrors: typeof errors = {};
    if (!producer) newErrors.producer = 'Producer is required';
    if (!region) newErrors.region = 'Region is required';
    if (!color) newErrors.color = 'Color is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const data = {
        producerId: producer!._id,
        regionId: region!._id,
        labelGrapeIds: labelGrapes.map((g) => g._id),
        specialName: specialName.trim() || undefined,
        color: color as WineColor,
        type: wineType,
        subType: subType || undefined,
        designation: designation.trim() || undefined,
        classification: classification.trim() || undefined,
        vintageConfig: vintageConfig || undefined,
        firstVintage: firstVintage ? parseInt(firstVintage, 10) : undefined,
        finalVintage: finalVintage ? parseInt(finalVintage, 10) : undefined,
        lwinId: lwinId.trim() || undefined,
        active,
      };

      if (isEditMode && id) {
        await wineNameService.update(id, data);
      } else {
        await wineNameService.create(data);
      }
      navigate('/admin/wine-names');
    } catch (error) {
      console.error('Failed to save wine name:', error);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const displayNamePreview = buildDisplayNamePreview(producer, region, labelGrapes, specialName);

  if (loadingPage) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      {creatingOnFly && (
        <Box
          sx={{
            position: 'fixed', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: alpha(theme.palette.background.default, 0.6),
            zIndex: 9999, backdropFilter: 'blur(4px)',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 3, borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          {isEditMode ? 'Edit Wine Name' : 'Create Wine Name'}
        </Typography>

        {duplicate && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
            <strong>Possible duplicate detected:</strong> "{duplicate.displayName}" already exists
            with the same producer, region, grapes, and special name.
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* ── Top: two-column identity + classification ── */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>

            {/* Left — naming fields */}
            <Box>
              {/* Producer */}
              <Autocomplete<ProducerOption>
                options={producerOptions}
                value={producer}
                loading={loadingProducers}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                getOptionLabel={(o) => o.__isNew__ ? o.name : (o.fullName || o.name)}
                filterOptions={(options, params) => {
                  const filtered = filterProducers(options, params);
                  const { inputValue } = params;
                  const exists = options.some((o) => (o.fullName || o.name).toLowerCase() === inputValue.toLowerCase());
                  if (inputValue !== '' && !exists) {
                    filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
                  }
                  return filtered;
                }}
                onInputChange={(_, val) => searchProducers(val)}
                onChange={handleProducerChange}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.__isNew__ && <CreateIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: option.__isNew__ ? 600 : 400 }}>
                          {option.__isNew__ ? option.name : (option.fullName || option.name)}
                        </Typography>
                        {option.needsReview && !option.__isNew__ && (
                          <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>Needs review</Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Producer"
                    required
                    margin="normal"
                    error={Boolean(errors.producer)}
                    helperText={errors.producer || 'Search or type to create'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{loadingProducers && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />

              {/* Region — now required */}
              <Autocomplete<RegionOption>
                options={regionOptions}
                value={region}
                loading={loadingRegions}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                getOptionLabel={(o) => o.name}
                filterOptions={(options, params) => {
                  const filtered = filterRegions(options, params);
                  const { inputValue } = params;
                  const exists = options.some((o) => o.name.toLowerCase() === inputValue.toLowerCase());
                  if (inputValue !== '' && !exists) {
                    filtered.push({ _id: '__new__', name: `Create "${inputValue}"`, __isNew__: true, inputValue });
                  }
                  return filtered;
                }}
                onInputChange={(_, val) => searchRegions(val)}
                onChange={handleRegionChange}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.__isNew__ && <CreateIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: option.__isNew__ ? 600 : 400 }}>
                          {option.name}
                        </Typography>
                        {!option.__isNew__ && option.hierarchyNames && option.hierarchyNames.length > 1 && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {option.hierarchyNames.join(' › ')}
                          </Typography>
                        )}
                        {option.needsReview && !option.__isNew__ && (
                          <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>Needs review</Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Region"
                    required
                    margin="normal"
                    error={Boolean(errors.region)}
                    helperText={errors.region || 'Search or type to create'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{loadingRegions && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />

              {/* Label Grapes */}
              <Autocomplete<GrapeOption, true>
                multiple
                options={grapeOptions}
                value={labelGrapes}
                loading={loadingGrapes}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                getOptionLabel={(o) => o.primaryName}
                filterOptions={(options, params) => {
                  const filtered = filterGrapes(options, params);
                  const { inputValue } = params;
                  const exists = options.some((o) => o.primaryName.toLowerCase() === inputValue.toLowerCase());
                  if (inputValue !== '' && !exists && labelGrapes.length < 3) {
                    filtered.push({ _id: '__new__', primaryName: `Create "${inputValue}"`, __isNew__: true, inputValue });
                  }
                  return filtered;
                }}
                onInputChange={(_, val) => searchGrapes(val)}
                onChange={handleGrapesChange}
                getOptionDisabled={() => labelGrapes.length >= 3}
                renderOption={(props, option) => (
                  <li {...props} key={option._id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.__isNew__ && <CreateIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: option.__isNew__ ? 600 : 400 }}>
                          {option.primaryName}
                        </Typography>
                        {!option.__isNew__ && option.color && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {option.color.replace('_', ' / ')}
                          </Typography>
                        )}
                        {option.needsReview && !option.__isNew__ && (
                          <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>Needs review</Typography>
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...other } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        label={option.primaryName}
                        size="small"
                        icon={option.needsReview ? <Tooltip title="Needs review"><WarningIcon sx={{ fontSize: '14px !important' }} /></Tooltip> : undefined}
                        {...other}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Label Grapes"
                    margin="normal"
                    helperText={labelGrapes.length >= 3 ? 'Maximum 3 grapes reached' : 'Optional — up to 3 grapes shown on the label'}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: <>{loadingGrapes && <CircularProgress size={16} />}{params.InputProps.endAdornment}</>,
                    }}
                  />
                )}
              />

              <TextField
                fullWidth
                label="Special Name"
                value={specialName}
                onChange={(e) => setSpecialName(e.target.value)}
                margin="normal"
                helperText='Optional — cuvée, vineyard, or proprietary name (e.g. "Reserva", "Grand Cru")'
              />
            </Box>

            {/* Right — classification fields + preview */}
            <Box>
              {/* Display Name Preview */}
              <Box
                sx={{
                  mt: 2, p: 2, borderRadius: 2,
                  backgroundColor: alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
              >
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: 0.5 }}>
                  DISPLAY NAME PREVIEW
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    mt: 0.5, fontWeight: 600,
                    color: displayNamePreview ? theme.palette.text.primary : theme.palette.text.disabled,
                    fontStyle: displayNamePreview ? 'normal' : 'italic',
                  }}
                >
                  {displayNamePreview || 'Fill in Producer, Region above to preview'}
                </Typography>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5, display: 'block' }}>
                  Final name is generated by the server
                </Typography>
              </Box>

              {checkingDuplicate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    Checking for duplicates…
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Color + Type side by side */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <FormControl required error={Boolean(errors.color)}>
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value as WineColor);
                      if (errors.color) setErrors((prev) => ({ ...prev, color: undefined }));
                    }}
                    label="Color"
                  >
                    {(Object.entries(colorLabels) as [WineColor, string][]).map(([val, label]) => (
                      <MenuItem key={val} value={val}>{label}</MenuItem>
                    ))}
                  </Select>
                  {errors.color && <FormHelperText>{errors.color}</FormHelperText>}
                </FormControl>

                <FormControl required>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={wineType}
                    onChange={(e) => setWineType(e.target.value as WineType)}
                    label="Type"
                  >
                    {(Object.entries(typeLabels) as [WineType, string][]).map(([val, label]) => (
                      <MenuItem key={val} value={val}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Sub Type */}
              <FormControl fullWidth margin="normal">
                <InputLabel>Sub Type</InputLabel>
                <Select
                  value={subType}
                  onChange={(e) => setSubType(e.target.value as WineSubType | '')}
                  label="Sub Type"
                  displayEmpty
                >
                  <MenuItem value="">— None —</MenuItem>
                  {(Object.entries(subTypeLabels) as [WineSubType, string][]).map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Designation + Classification */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                <TextField
                  label="Designation"
                  value={designation}
                  onChange={(e) => setDesignation(e.target.value)}
                  helperText="e.g. DOC, AOP, AVA"
                />
                <TextField
                  label="Classification"
                  value={classification}
                  onChange={(e) => setClassification(e.target.value)}
                  helperText="e.g. Grand Cru, Premier Cru"
                />
              </Box>
            </Box>
          </Box>

          {/* ── Vintage section (full width) ── */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" sx={{ mb: 2, color: theme.palette.text.secondary, fontWeight: 600, letterSpacing: 0.5 }}>
            VINTAGE
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
            <FormControl>
              <InputLabel>Vintage Config</InputLabel>
              <Select
                value={vintageConfig}
                onChange={(e) => setVintageConfig(e.target.value as VintageConfig | '')}
                label="Vintage Config"
                displayEmpty
              >
                <MenuItem value="">— None —</MenuItem>
                <MenuItem value="SEQUENTIAL">Sequential</MenuItem>
                <MenuItem value="NON_SEQUENTIAL">Non-Sequential</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="First Vintage"
              type="number"
              value={firstVintage}
              onChange={(e) => setFirstVintage(e.target.value)}
              inputProps={{ min: 1600, max: 2100, step: 1 }}
              helperText="Year (e.g. 2005)"
            />

            <TextField
              label="Final Vintage"
              type="number"
              value={finalVintage}
              onChange={(e) => setFinalVintage(e.target.value)}
              inputProps={{ min: 1600, max: 2100, step: 1 }}
              helperText="Leave empty if still in production"
            />

            <TextField
              label="LWIN ID"
              value={lwinId}
              onChange={(e) => setLwinId(e.target.value)}
              helperText="Liv-ex wine identifier"
            />
          </Box>

          {/* ── Active toggle + actions ── */}
          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} color="primary" />}
              label="Active"
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saving || creatingOnFly}
              >
                {saving ? 'Saving…' : isEditMode ? 'Save Changes' : 'Create Wine Name'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={() => navigate('/admin/wine-names')}
                disabled={saving}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default WineNameForm;
